import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { CheckCircle2, AlertCircle, TrendingUp, Lightbulb, Star, Sparkles } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

const FeedbackShowcase = () => {
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.2 });
  const [animatedScores, setAnimatedScores] = useState<number[]>([0, 0, 0, 0, 0, 0, 0]);
  const [showCoachingTip, setShowCoachingTip] = useState(false);
  const isMobile = useIsMobile();

  // Memoize particles to avoid recreating on every render - reduce count on mobile
  const particles = useMemo(() => {
    const count = isMobile ? 5 : 15;
    return [...Array(count)].map((_, i) => ({
      id: i,
      left: `${10 + Math.random() * 80}%`,
      top: `${Math.random() * 100}%`,
      duration: `${4 + Math.random() * 3}s`,
      delay: `${Math.random() * 5}s`,
    }));
  }, [isMobile]);

  const rubricScores = [
    { category: 'Opening', score: 85, color: 'emerald' },
    { category: 'Needs Identification', score: 78, color: 'primary' },
    { category: 'Solution Presentation', score: 72, color: 'amber' },
    { category: 'Objection Handling', score: 88, color: 'emerald' },
    { category: 'Close', score: 65, color: 'rose' },
    { category: 'Communication', score: 82, color: 'primary' },
    { category: 'Overall', score: 78, color: 'primary' },
  ];

  // Animate scores when visible
  useEffect(() => {
    if (!isVisible) return;
    
    const duration = 1200;
    const steps = 30;
    const interval = duration / steps;
    
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const eased = 1 - Math.pow(1 - progress, 3);
      
      setAnimatedScores(rubricScores.map(item => Math.round(item.score * eased)));
      
      if (step >= steps) {
        clearInterval(timer);
        setTimeout(() => setShowCoachingTip(true), 300);
      }
    }, interval);
    
    return () => clearInterval(timer);
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="py-20 sm:py-32 relative overflow-hidden">
      {/* Background with animated gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-accent/5 to-transparent" />
      
      {/* Animated particles - reduced on mobile */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute w-1 h-1 bg-emerald-400/20 rounded-full will-change-transform"
            style={{
              left: particle.left,
              top: particle.top,
              animation: `float ${particle.duration} ease-in-out infinite`,
              animationDelay: particle.delay,
            }}
          />
        ))}
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left side - Feedback mockup */}
          <div className={`relative order-2 lg:order-1 transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            {/* Glow - reduced blur on mobile */}
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 via-primary/20 to-amber-500/20 blur-xl md:blur-3xl opacity-40 scale-110 md:animate-pulse-glow will-change-transform" />
            
            {/* Feedback card mockup */}
            <div className="relative glass rounded-2xl sm:rounded-3xl overflow-hidden border border-white/10 hover:border-white/20 transition-all duration-300">
              {/* Header */}
              <div className="px-5 py-4 bg-background/50 border-b border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-400" />
                    <h3 className="font-display font-semibold text-foreground">Session Feedback</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex">
                      {[1,2,3,4].map(i => (
                        <Star 
                          key={i} 
                          className={`w-4 h-4 transition-all duration-300 ${isVisible ? 'text-amber-400 fill-amber-400' : 'text-muted'}`}
                          style={{ transitionDelay: `${800 + i * 100}ms` }}
                        />
                      ))}
                      <Star 
                        className={`w-4 h-4 transition-all duration-300 ${isVisible ? 'text-amber-400 fill-amber-400/50' : 'text-muted'}`}
                        style={{ transitionDelay: `1200ms` }}
                      />
                    </div>
                    <span className="font-display text-2xl font-bold text-foreground">{animatedScores[6]}</span>
                    <span className="text-muted-foreground">/100</span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">MC Bliss Tray - Heritage Vineyards</p>
              </div>

              {/* Scores with animated bars */}
              <div className="p-5 space-y-3 bg-background/60">
                {rubricScores.map((item, index) => (
                  <div 
                    key={item.category}
                    className={`group transition-all duration-500 hover:bg-foreground/5 rounded-lg p-2 -mx-2 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
                    style={{ transitionDelay: `${200 + index * 80}ms` }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">{item.category}</span>
                      <span className={`text-sm font-semibold transition-all duration-300 ${
                        animatedScores[index] >= 80 ? 'text-emerald-400' : 
                        animatedScores[index] >= 70 ? 'text-primary' : 
                        animatedScores[index] >= 60 ? 'text-amber-400' : 'text-rose-400'
                      }`}>
                        {animatedScores[index]}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-muted/30 overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-1000 ease-out relative overflow-hidden ${
                          item.score >= 80 ? 'bg-emerald-400' : 
                          item.score >= 70 ? 'bg-primary' : 
                          item.score >= 60 ? 'bg-amber-400' : 'bg-rose-400'
                        }`}
                        style={{ 
                          width: isVisible ? `${item.score}%` : '0%',
                          transitionDelay: `${400 + index * 100}ms`
                        }}
                      >
                        {/* Shimmer effect on bars */}
                        <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Coaching tip with entrance animation */}
              <div className={`px-5 py-4 bg-primary/5 border-t border-white/10 transition-all duration-500 ${showCoachingTip ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0 animate-pulse-glow">
                    <Lightbulb className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-foreground mb-1">Judge's Coaching Tip</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Strong discovery questions! Focus on transitioning to your close more naturally. 
                      Try summarizing the buyer's needs before asking for commitment.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Text content */}
          <div className={`order-1 lg:order-2 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-sm font-medium text-emerald-400 mb-4">
              <CheckCircle2 className="w-4 h-4" />
              Objective Intelligence
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
              Know Exactly{' '}
              <span className="relative inline-block">
                <span className="text-gradient bg-gradient-to-r from-emerald-400 via-primary to-accent bg-clip-text text-transparent animate-gradient bg-[length:200%_auto]">
                  Where to Improve
                </span>
              </span>
            </h2>
            <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
              Get scored on multiple rubric categories with specific, actionable feedback.
            </p>

            <div className="space-y-4">
              {[
                { icon: CheckCircle2, title: 'Rubric-Aligned Scoring', desc: 'Every category matches the official competition rubric exactly', color: 'emerald' },
                { icon: TrendingUp, title: 'SPIN Breakdown', desc: 'See detailed analysis of your Situation, Problem, Implication, and Need-Payoff questions', color: 'primary' },
                { icon: AlertCircle, title: 'Specific Improvements', desc: 'Not generic advice. Feedback references exactly what you said and how to improve it', color: 'amber' },
              ].map((feature, index) => (
                <div 
                  key={feature.title}
                  className={`flex items-start gap-4 p-3 rounded-xl transition-all duration-500 hover:bg-foreground/5 group ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                  style={{ transitionDelay: `${400 + index * 100}ms` }}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110 ${
                    feature.color === 'emerald' ? 'bg-emerald-400/10 text-emerald-400' :
                    feature.color === 'amber' ? 'bg-amber-400/10 text-amber-400' :
                    'bg-primary/10 text-primary'
                  }`}>
                    <feature.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground mb-1" role="heading" aria-level={4}>{feature.title}</p>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeedbackShowcase;